import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import LeLiveHeading from './LeLiveHeading'
import TextButton from 'LuxKit/Button/TextButton'
import LineCalendarAltIcon from 'LuxKit/Icons/line/LineCalendarAltIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import Image from 'components/Common/Image'
import LeLiveGetTicketsButton from './LeLiveGetTicketsButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import useToggle from 'hooks/useToggle'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const ICAL_CONTENT = [
  'BEGIN:VCALENDAR',
  'VERSION:2.0',
  'PRODID:-//Luxury Escapes//LE Live 2026//EN',
  'BEGIN:VEVENT',
  'DTSTART;VALUE=DATE:20260516',
  'DTEND;VALUE=DATE:20260518',
  'SUMMARY:Luxury Escapes Live 2026',
  'DESCRIPTION:Australia\'s ultimate luxury travel event. Royal Exhibition Building\\, Melbourne.',
  'LOCATION:Royal Exhibition Building\\, 9 Nicholson St\\, Carlton VIC 3053',
  'URL:https://www.luxuryescapes.com/au/live',
  'END:VEVENT',
  'END:VCALENDAR',
].join('\r\n')

const GOOGLE_CALENDAR_URL = 'https://calendar.google.com/calendar/render?' + new URLSearchParams({
  action: 'TEMPLATE',
  text: 'Luxury Escapes Live 2026',
  dates: '20260516/20260518',
  details: "Australia's ultimate luxury travel event.\nhttps://www.luxuryescapes.com/au/live",
  location: 'Royal Exhibition Building, 9 Nicholson St, Carlton VIC 3053',
}).toString()

const OUTLOOK_CALENDAR_URL = 'https://outlook.live.com/calendar/0/action/compose?' + new URLSearchParams({
  subject: 'Luxury Escapes Live 2026',
  startdt: '2026-05-16',
  enddt: '2026-05-18',
  body: "Australia's ultimate luxury travel event.\nhttps://www.luxuryescapes.com/au/live",
  location: 'Royal Exhibition Building, 9 Nicholson St, Carlton VIC 3053',
  allday: 'true',
  path: '/calendar/action/compose',
}).toString()

function useIcalBlobUrl(): string | undefined {
  const [url, setUrl] = useState<string>()

  useEffect(() => {
    if (typeof Blob === 'undefined') return
    const blobUrl = URL.createObjectURL(new Blob([ICAL_CONTENT], { type: 'text/calendar' }))
    setUrl(blobUrl)
    return () => URL.revokeObjectURL(blobUrl)
  }, [])

  return url
}

const SectionWrapper = styled.section`
  background-color: #f3f1ea;
`

const ImageWrapper = styled.div`
  border-radius: ${rem(8)};
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    object-fit: cover;
  }

  ${mediaQueryUp.desktop} {
    flex: 1 0 0;
    min-width: 0;
    align-self: stretch;

    img {
      height: 100%;
    }
  }
`

const TextContainer = styled(Group)`
  ${mediaQueryUp.desktop} {
    flex: 1 0 0;
    min-width: 0;
    padding-left: ${rem(59)};
    padding-right: ${rem(96)};
  }
`

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  background-color: #ffffff;
  border-radius: 50%;
  color: #363a45;
  flex-shrink: 0;
  box-shadow:
    0 1px 1.5px rgba(0, 0, 0, 0.07),
    0 1.5px 5px rgba(0, 0, 0, 0.05),
    0 5px 10px rgba(0, 0, 0, 0.05),
    0 15px 32px rgba(0, 0, 0, 0.04);
`

interface Props {
  hasRegistration: boolean
}

function LeLiveEventDetails({ hasRegistration }: Props) {
  const intl = useIntl()
  const icalBlobUrl = useIcalBlobUrl()
  const calendarButtonRef = useRef<HTMLButtonElement>(null)
  const { value: calendarDropdownOpen, toggle: toggleCalendarDropdown, off: closeCalendarDropdown } = useToggle()

  return (
    <SectionWrapper id="event-details">
      <Sheet padding="56 20" desktopPadding="64 0">
        <LayoutContainer>
          <Group
            direction="vertical"
            gap={32}
            desktopDirection="horizontal"
            desktopGap={0}
            desktopVerticalAlign="stretch"
          >
            <ImageWrapper>
              <Image
                id="gma2bf9harb2yvqf1fva"
                alt="Royal Exhibition Building, Melbourne"
                width={640}
                height={430}
                dpr={2}
                enhancedFormats
                fit="center"
                objectFit="cover"
              />
            </ImageWrapper>

            <TextContainer direction="vertical" gap={32} desktopGap={40} desktopVerticalAlign="center">
              <LeLiveHeading variant="heading1alt" as="h2">
                {intl.formatMessage({
                  id: 'KqtR2rvN4L84t4tG52v3',
                  defaultMessage: 'Event details',
                  description: 'Section heading for the event details area on the LE Live event landing page',
                })}
              </LeLiveHeading>

              <Group direction="vertical" gap={24}>
                <Group direction="horizontal" gap={16}>
                  <IconCircle>
                    <LineCalendarAltIcon />
                  </IconCircle>
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="large" weight="bold">
                      {intl.formatMessage({
                        id: 'OQDMgNEm58a28mpFcvIX',
                        defaultMessage: 'When',
                        description: 'Bold label for the date and time information row in the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="large">
                      {intl.formatMessage({
                        id: 'efHwabwsZDUB48zEHcSr',
                        defaultMessage: 'Saturday 16 May & Sunday 17 May 2026',
                        description: 'Specific calendar dates displayed in the event details section of the LE Live event page',
                      })}
                    </BodyText>
                    <BodyText variant="medium" colour="neutral-two">
                      {intl.formatMessage({
                        id: 'I4ArKNl7mRlm62-XNi_C',
                        defaultMessage: '10:00am – 6:00pm',
                        description: 'Opening and closing times displayed in the event details section of the LE Live event page',
                      })}
                    </BodyText>
                  </Group>
                </Group>

                <Group direction="horizontal" gap={16}>
                  <IconCircle>
                    <LineMapMarkerIcon />
                  </IconCircle>
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="large" weight="bold">
                      {intl.formatMessage({
                        id: 'KBcAFSh7h7995VqZm2kq',
                        defaultMessage: 'Where',
                        description: 'Bold label for the location information row in the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="large">
                      {intl.formatMessage({
                        id: '86Usnz2CXGqvu-brgn3B',
                        defaultMessage: 'Royal Exhibition Building',
                        description: 'Name of the venue displayed in the location row of the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="medium" colour="neutral-two">
                      {intl.formatMessage({
                        id: 'NRelv2BsbqzSKZdJ7MnK',
                        defaultMessage: '9 Nicholson St, Carlton VIC 3053',
                        description: 'Street address of the venue displayed in the location row of the LE Live event details section',
                      })}
                    </BodyText>
                  </Group>
                </Group>

                <Group direction="horizontal" gap={16}>
                  <IconCircle>
                    <LineTicketIcon />
                  </IconCircle>
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="large" weight="bold">
                      {intl.formatMessage({
                        id: '_JZhTGmZ5AoXLtCjoyjs',
                        defaultMessage: 'Tickets',
                        description: 'Bold label for the tickets and pricing row in the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="large">
                      {intl.formatMessage({
                        id: 'ljnYEr7u66foX_3ZfCad',
                        defaultMessage: 'FREE entry for members; $29 door entry',
                        description: 'Summary of ticket pricing for members and walk-ups in the LE Live event details section',
                      })}
                    </BodyText>
                  </Group>
                </Group>
              </Group>

              <Group direction="vertical" desktopDirection="horizontal" gap={20} desktopGap={12}>
                <LeLiveGetTicketsButton hidden={hasRegistration} />
                <TextButton
                  kind="secondary"
                  size="large"
                  fit="mobile-full-width"
                  startIcon={<LineCalendarAltIcon />}
                  ref={calendarButtonRef}
                  onClick={toggleCalendarDropdown}
                >
                  {intl.formatMessage({
                    id: 'd3reCYWNt8SwDZ63X3DR',
                    defaultMessage: 'Add to calendar',
                    description: 'Button label to open the add-to-calendar dropdown in the LE Live event details section',
                  })}
                </TextButton>
                <DropdownList
                  size="S"
                  anchorRef={calendarButtonRef}
                  triggerRef={calendarButtonRef}
                  open={calendarDropdownOpen}
                  onClose={closeCalendarDropdown}
                  placement="top"
                >
                  <ClickableListItem
                    title={intl.formatMessage({
                      id: 'xEBtcPtkaR42pTjsx5rr',
                      defaultMessage: 'Google Calendar',
                      description: 'Dropdown menu option to add the LE Live event to Google Calendar from the event details section',
                    })}
                    href={GOOGLE_CALENDAR_URL}
                    target="_blank"
                    onClick={closeCalendarDropdown}
                  />
                  <ClickableListItem
                    title={intl.formatMessage({
                      id: 'wiTqxSSrOScfS_CYiyWH',
                      defaultMessage: 'Outlook',
                      description: 'Dropdown menu option to add the LE Live event to Outlook Calendar from the event details section',
                    })}
                    href={OUTLOOK_CALENDAR_URL}
                    target="_blank"
                    onClick={closeCalendarDropdown}
                  />
                  <ClickableListItem
                    title={intl.formatMessage({
                      id: 'h3so7ya93dejeoppjUCu',
                      defaultMessage: 'Download event calendar',
                      description: 'Dropdown menu option to download an .ics calendar file for the LE Live event from the details section',
                    })}
                    href={icalBlobUrl}
                    download="luxury-escapes-live-2026.ics"
                    onClick={closeCalendarDropdown}
                  />
                </DropdownList>
              </Group>
            </TextContainer>
          </Group>
        </LayoutContainer>
      </Sheet>
    </SectionWrapper>
  )
}

export default LeLiveEventDetails
